import React, { useState, useEffect } from 'react';
import Icon from '../Shared/Icons';
import { db } from '../../config/firebase';
import {
  collection, addDoc, serverTimestamp,
  getDocs, query, where, doc, getDoc
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';

export default function OnlinePayment() {
  const { currentUser } = useAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [donations, setDonations] = useState([]);
  const [activeTab, setActiveTab] = useState('donate');

  const [form, setForm] = useState({
    amount: '',
    currency: 'USD',
    paymentMethod: 'mpesa',
    message: '',
    senderName: '',
    senderPhone: '',
    transactionId: '',
  });

  useEffect(() => {
    fetchUserInfo();
    fetchDonations();
  }, []);

  async function fetchUserInfo() {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserInfo(docSnap.data());
        setForm(prev => ({
          ...prev,
          senderName: `${docSnap.data().firstName || ''} ${docSnap.data().lastName || ''}`.trim(),
          senderPhone: docSnap.data().phone || '',
        }));
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchDonations() {
    try {
      const q = query(
        collection(db, 'donations'),
        where('donorId', '==', currentUser.uid)
      );
      const snap = await getDocs(q);
      setDonations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.amount || Number(form.amount) < 1) {
      setError('Please select a donation amount.');
      return;
    }
    if (!form.transactionId.trim()) {
      setError('Please enter your transaction ID or reference number after sending!');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'donations'), {
        donorId: currentUser.uid,
        donorName: form.senderName,
        donorEmail: userInfo?.email || currentUser.email,
        donorPhone: form.senderPhone,
        amount: Number(form.amount),
        currency: form.currency,
        message: form.message,
        paymentMethod: form.paymentMethod,
        transactionId: form.transactionId,
        status: 'Pending Verification',
        createdAt: serverTimestamp(),
      });

      setSuccess(
        `Thank you for your donation! We have received your submission and will verify your payment of ${form.currency} ${Number(form.amount).toLocaleString()} shortly.`
      );
      setForm(prev => ({
        ...prev,
        amount: '',
        currency: 'USD',
            message: '',
        transactionId: '',
      }));
      fetchDonations();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  const paymentMethods = [
    { id: 'mpesa', label: 'M-Pesa', color: '#00A651', icon: 'bolt', number: '+255 XXX XXX XXX', instruction: 'Go to M-Pesa → Lipa na M-Pesa → Enter number → Enter amount → Enter PIN' },
    { id: 'airtel', label: 'Airtel Money', color: '#FF0000', icon: 'bolt', number: '+255 XXX XXX XXX', instruction: 'Go to Airtel Money → Send Money → Enter number → Enter amount → Confirm' },
    { id: 'tigo', label: 'Tigo Pesa', color: '#0099CC', icon: 'bolt', number: '+255 XXX XXX XXX', instruction: 'Go to Tigo Pesa → Tuma Pesa → Enter number → Enter amount → Confirm' },
    { id: 'halotel', label: 'Halotel', color: '#FF6600', icon: 'bolt', number: '+255 XXX XXX XXX', instruction: 'Go to HaloPesa → Tuma Pesa → Enter number → Enter amount → Confirm' },
    { id: 'bank', label: 'Bank Transfer', color: '#14452F', icon: 'card', number: 'Account: XXXX XXXX XXXX', instruction: 'Transfer to our bank account and use your name as reference' },
  ];

  const selectedMethod = paymentMethods.find(m => m.id === form.paymentMethod);


  // ── CHANGED: $19/month and $228/year only ──
  const quickAmounts = [
    {
      label: '$19 / month',
      sublabel: 'Academic support · Stationery · Sports · Uniform',
      value: '19',
      currency: 'USD',
      badge: null,
    },
    {
      label: '$228 / year',
      sublabel: 'Full year of support — save $0 vs monthly',
      value: '228',
      currency: 'USD',
      badge: 'Best Value',
    },
  ];

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Donate Online</div>
            <div style={styles.pageSub}>Support Iyanna Foundation — every donation changes a child's life</div>
          </div>
        </div>

        {/* TABS */}
        <div style={styles.tabs}>
          {[
            { id: 'donate', label: 'Make Donation' },
            { id: 'history', label: 'My Donations' },
          ].map(tab => (
            <button key={tab.id}
              style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* DONATE TAB */}
        {activeTab === 'donate' && (
          <div style={styles.grid}>

            {/* LEFT — FORM */}
            <div style={styles.formSection}>

              {/* IMPACT CARD */}
              <div style={styles.impactCard}>
                <div style={styles.impactTitle}>What Your $19/Month Covers</div>
                <div style={styles.impactList}>
                  {[
                    { icon: 'students', text: 'Academic support for the full term' },
                    { icon: 'book', text: 'Stationery & books' },
                    { icon: '⚽', text: 'Sports & extracurricular activities' },
                    { icon: '👕', text: 'School uniform' },
                  ].map((item, i) => (
                    <div key={i} style={styles.impactItem}>
                      <span style={styles.impactIcon}><Icon name={item.icon} size={16} color="currentColor" /></span>
                      <span style={styles.impactText}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {success && (
                <div style={styles.successMsg}>
                  <div style={styles.successIcon}></div>
                  <div>{success}</div>
                </div>
              )}
              {error && <div style={styles.errorMsg}>{error}</div>}

              <form onSubmit={handleSubmit}>

                {/* STEP 1 — AMOUNT */}
                <div style={styles.panel}>
                  <div style={styles.sectionTitle}>Step 1 — Choose Your Plan</div>

                  {/* ── CHANGED: 2 plan cards instead of quick amount grid ── */}
                  <div style={styles.planGrid}>
                    {quickAmounts.map((q, i) => {
                      const isSelected = form.amount === q.value && form.currency === q.currency;
                      return (
                        <div key={i}
                          style={{ ...styles.planCard, ...(isSelected ? styles.planCardActive : {}) }}
                          onClick={() => setForm({ ...form, amount: q.value, currency: q.currency })}>
                          {q.badge && <div style={styles.planBadge}>{q.badge}</div>}
                          <div style={{ ...styles.planAmount, color: isSelected ? '#14452F' : '#1C1C1E' }}>
                            {q.label}
                          </div>
                          <div style={styles.planSublabel}>{q.sublabel}</div>
                          {isSelected && <div style={styles.planCheck}>✓ Selected</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* STEP 2 — PAYMENT METHOD */}
                <div style={styles.panel}>
                  <div style={styles.sectionTitle}>Step 2 — Choose Payment Method</div>
                  <div style={styles.methodGrid}>
                    {paymentMethods.map(method => (
                      <div key={method.id}
                        style={{
                          ...styles.methodCard,
                          ...(form.paymentMethod === method.id ? { border: `2px solid ${method.color}`, background: `${method.color}15` } : {})
                        }}
                        onClick={() => setForm({ ...form, paymentMethod: method.id })}>
                        <div style={styles.methodIcon}><Icon name={method.icon} size={16} color="currentColor" /></div>
                        <div style={{ ...styles.methodLabel, color: form.paymentMethod === method.id ? method.color : '#374151' }}>
                          {method.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedMethod && (
                    <div style={{ ...styles.instructionBox, borderColor: selectedMethod.color, background: `${selectedMethod.color}10` }}>
                      <div style={styles.instructionTitle}>How To Pay via {selectedMethod.label}</div>
                      <div style={styles.instructionNumber}>
                        <span style={styles.instructionLabel}>Send to:</span>
                        <span style={{ ...styles.instructionValue, color: selectedMethod.color }}>{selectedMethod.number}</span>
                      </div>
                      <div style={styles.instructionSteps}>{selectedMethod.instruction}</div>
                    </div>
                  )}
                </div>

                {/* STEP 3 — MESSAGE */}
                <div style={styles.panel}>
                  <div style={styles.sectionTitle}>Step 3 — Leave a Message (Optional)</div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Personal Message</label>
                    <textarea
                      style={{ ...styles.formInput, height: '80px', resize: 'vertical' }}
                      name="message" value={form.message} onChange={handleChange}
                      placeholder="Leave an encouraging message for the children..." />
                  </div>
                </div>
                {/* STEP 4 — CONFIRM */}
                <div style={styles.panel}>
                  <div style={styles.sectionTitle}>Step 4 — Confirm Your Payment</div>
                  <div style={styles.confirmNote}>
                    ℹ️ After sending your payment please enter your transaction ID below so we can verify your donation.
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Your Name *</label>
                    <input style={styles.formInput} name="senderName" value={form.senderName} onChange={handleChange} placeholder="Your full name" required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Your Phone Number *</label>
                    <input style={styles.formInput} name="senderPhone" value={form.senderPhone} onChange={handleChange} placeholder="+255 7XX XXX XXX" required />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Transaction ID / Reference Number *</label>
                    <input style={styles.formInput} name="transactionId" value={form.transactionId} onChange={handleChange} placeholder="e.g. RGH8XXXXXXX or your reference" required />
                    <div style={styles.inputHint}>You receive this after sending payment</div>
                  </div>
                </div>

                {/* SUMMARY */}
                {form.amount && (
                  <div style={styles.summaryCard}>
                    <div style={styles.summaryTitle}>Donation Summary</div>
                    {[
                      { label: 'Amount', value: `${form.currency} ${Number(form.amount).toLocaleString()}` },
                      { label: 'Plan', value: form.amount === '228' ? 'Annual (Best Value)' : 'Monthly' },
                      { label: 'Payment Method', value: selectedMethod?.label || '' },
                
                    ].map((row, i) => (
                      <div key={i} style={styles.summaryRow}>
                        <span style={styles.summaryLabel}>{row.label}</span>
                        <span style={styles.summaryValue}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button type="submit" style={styles.donateBtn} disabled={loading || !form.amount}>
                  {loading
                    ? 'Submitting...'
                    : `Submit Donation of ${form.currency} ${form.amount ? Number(form.amount).toLocaleString() : ''}`}
                </button>

                <div style={styles.secureNote}>
                  Your donation is safe and secure. We verify all payments within 24 hours.
                </div>

              </form>
            </div>

            {/* RIGHT — IMPACT STATS */}
            <div style={styles.rightSection}>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Program Impact</div>
                {[
                  { icon: 'students', label: 'Children Supported', color: '#14452F' },
                  { icon: 'parents', label: 'Families Helped', color: '#F57C00' },
                  { icon: 'calendar', label: 'Saturday Sessions', color: '#4527A0' },
                  { icon: 'chess', label: 'Chess Students', color: '#00695C' },
                ].map((stat, i) => (
                  <div key={i} style={styles.impactStatCard}>
                    <div style={{ ...styles.impactStatIcon, color: stat.color }}><Icon name={stat.icon} size={16} color="currentColor" /></div>
                    <div style={styles.impactStatInfo}>
                      <div style={styles.impactStatLabel}>{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Payment Numbers</div>
                {paymentMethods.map((m, i) => (
                  <div key={i} style={styles.paymentNumberRow}>
                    <span style={{ ...styles.paymentMethodLabel, color: m.color }}><Icon name={m.icon} size={16} color="currentColor" /> {m.label}</span>
                    <span style={styles.paymentNumber}>{m.number}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>My Donation History</div>
            {donations.length === 0 ? (
              <div style={styles.empty}>
                <div style={styles.emptyIcon}></div>
                <div style={styles.emptyText}>No donations yet. Make your first donation!</div>
                <button style={styles.donateBtn} onClick={() => setActiveTab('donate')}>Make a Donation →</button>
              </div>
            ) : (
              donations.map((d, i) => (
                <div key={i} style={styles.donationItem}>
                  <div style={styles.donationIconBox}></div>
                  <div style={styles.donationInfo}>
                    <div style={styles.donationAmount}>{d.currency} {Number(d.amount).toLocaleString()}</div>
                    <div style={styles.donationMeta}>
                      {d.paymentMethod?.toUpperCase()} · {d.createdAt?.toDate?.().toLocaleDateString() || 'Recently'}
                    </div>
                  </div>
                  <span style={{ ...styles.donationStatus, background: d.status === 'Verified' ? '#EAF3EC' : '#FFF3E0', color: d.status === 'Verified' ? '#14452F' : '#E65100' }}>
                    {d.status === 'Verified' ? '' : ''} {d.status}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: { marginBottom: '20px' },
  pageTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: { padding: '10px 20px', borderRadius: '20px', border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '500' },
  tabActive: { background: 'linear-gradient(135deg, #14452F, #1F6B45)', borderColor: 'transparent', color: '#fff' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '20px', alignItems: 'start' },
  formSection: { display: 'flex', flexDirection: 'column', gap: '16px' },
  rightSection: { display: 'flex', flexDirection: 'column', gap: '16px' },
  impactCard: { background: 'linear-gradient(135deg, #041F14, #14452F)', borderRadius: '16px', padding: '20px', color: '#fff' },
  impactTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '15px', marginBottom: '14px', color: '#fff' },
  impactList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  impactItem: { display: 'flex', gap: '10px', alignItems: 'center' },
  impactIcon: { fontSize: '18px', width: '28px', textAlign: 'center', flexShrink: 0 },
  impactText: { fontSize: '13px', color: 'rgba(255,255,255,0.85)' },
  panel: { background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)' },
  sectionTitle: { fontSize: '11px', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6B7280', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6', fontWeight: '600' },
  // Plan cards — NEW
  planGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  planCard: { position: 'relative', padding: '18px 16px', borderRadius: '14px', border: '2px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', transition: 'all 0.15s' },
  planCardActive: { border: '2px solid #14452F', background: '#EAF3EC' },
  planBadge: { position: 'absolute', top: '-10px', right: '12px', background: '#F57C00', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px' },
  planAmount: { fontSize: '17px', fontWeight: '800', marginBottom: '6px' },
  planSublabel: { fontSize: '11.5px', color: '#6B7280', lineHeight: 1.5 },
  planCheck: { marginTop: '8px', fontSize: '12px', fontWeight: '700', color: '#14452F' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  formLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  formInput: { padding: '11px 14px', borderRadius: '9px', border: '1.5px solid #E5E7EB', background: '#FAF9F5', fontFamily: 'inherit', fontSize: '13.5px', color: '#1C1C1E', outline: 'none' },
  inputHint: { fontSize: '11px', color: '#9CA3AF', marginTop: '3px' },
  methodGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' },
  methodCard: { padding: '14px 10px', borderRadius: '12px', border: '1.5px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' },
  methodIcon: { fontSize: '22px', marginBottom: '6px' },
  methodLabel: { fontSize: '12px', fontWeight: '600' },
  instructionBox: { borderRadius: '12px', padding: '16px', border: '1.5px solid', marginTop: '4px' },
  instructionTitle: { fontSize: '13px', fontWeight: '600', color: '#1C1C1E', marginBottom: '10px' },
  instructionNumber: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' },
  instructionLabel: { fontSize: '12px', color: '#6B7280' },
  instructionValue: { fontSize: '14px', fontWeight: '700' },
  instructionSteps: { fontSize: '12.5px', color: '#374151', lineHeight: 1.5 },
  confirmNote: { background: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3', padding: '12px 14px', borderRadius: '10px', fontSize: '12.5px', marginBottom: '16px', lineHeight: 1.5 },
  summaryCard: { background: 'linear-gradient(135deg, #EAF3EC, #F1F8E9)', borderRadius: '14px', padding: '18px', border: '1px solid #BBD8C4', marginBottom: '16px' },
  summaryTitle: { fontSize: '13px', fontWeight: '600', color: '#14452F', marginBottom: '12px' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(20,69,47,0.1)', fontSize: '13px' },
  summaryLabel: { color: '#6B7280' },
  summaryValue: { fontWeight: '600', color: '#14452F' },
  donateBtn: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #F57C00, #FFA726)', color: '#fff', border: 'none', borderRadius: '12px', fontFamily: 'inherit', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 6px 20px rgba(245,124,0,0.35)', marginBottom: '12px' },
  secureNote: { textAlign: 'center', fontSize: '12px', color: '#6B7280', marginBottom: '20px' },
  successMsg: { background: '#EAF3EC', border: '1px solid #BBD8C4', color: '#14452F', padding: '16px', borderRadius: '12px', fontSize: '13.5px', display: 'flex', alignItems: 'flex-start', gap: '12px', lineHeight: 1.6 },
  successIcon: { fontSize: '24px', flexShrink: 0 },
  errorMsg: { background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', padding: '12px 16px', borderRadius: '10px', fontSize: '13px' },
  impactStatCard: { display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0', borderBottom: '1px solid #F3F4F6' },
  impactStatIcon: { fontSize: '28px', width: '44px', height: '44px', borderRadius: '12px', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  impactStatInfo: { flex: 1 },
  impactStatValue: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '22px', fontWeight: '700' },
  impactStatLabel: { fontSize: '12px', color: '#6B7280', marginTop: '2px' },
  paymentNumberRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6' },
  paymentMethodLabel: { fontSize: '13px', fontWeight: '600' },
  paymentNumber: { fontSize: '12px', color: '#6B7280' },
  empty: { textAlign: 'center', padding: '40px 20px' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyText: { fontSize: '13.5px', color: '#6B7280', marginBottom: '20px' },
  donationItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: '1px solid #F3F4F6' },
  donationIconBox: { width: '40px', height: '40px', borderRadius: '10px', background: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 },
  donationInfo: { flex: 1 },
  donationAmount: { fontSize: '14px', fontWeight: '600', color: '#1C1C1E' },
  donationMeta: { fontSize: '11.5px', color: '#6B7280', marginTop: '2px' },
  donationStatus: { fontSize: '11px', padding: '4px 10px', borderRadius: '20px', fontWeight: '500', flexShrink: 0 },
};